import { memo, useId } from 'react';

import { useWorkspaceAgentPromptCardCarouselScroll } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/carousel/useWorkspaceAgentPromptCardCarouselScroll';
import { WorkspaceAgentPromptCardCarouselControls } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/carousel/WorkspaceAgentPromptCardCarouselControls';
import { WorkspaceAgentPromptCardCarouselSlide } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/carousel/WorkspaceAgentPromptCardCarouselSlide';
import { type WorkspaceAgentPromptCardThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/prompt-cards/WorkspaceAgentPromptCardImageStack';

export type WorkspaceAgentPromptCardCarouselItem = {
  buttonLabel: string;
  id: string;
  thumbnails: readonly WorkspaceAgentPromptCardThumbnail[];
  subtitle: string;
  title: string;
};

export type WorkspaceAgentPromptCardCarouselProps = {
  cards: readonly WorkspaceAgentPromptCardCarouselItem[];
  isDisabled: boolean;
  onSelect: (id: string) => void;
};

/**
 * Horizontally scrolling prompt cards that snap to their start edge. The next card peeks so the row
 * reads as scrollable without a visible scrollbar. Cards stagger in like the zero-state chips.
 */
export const WorkspaceAgentPromptCardCarousel = memo(
  ({ cards, isDisabled, onSelect }: WorkspaceAgentPromptCardCarouselProps) => {
    const labelId = useId();
    const listId = useId();
    const {
      autoAdvancePauseProps,
      canScrollNext,
      canScrollPrev,
      handleScrollNext,
      handleScrollPrev,
      scrollContainerRef,
    } = useWorkspaceAgentPromptCardCarouselScroll();

    return (
      <div
        className="flex flex-col gap-2"
        data-testid="WORKSPACE_AGENT_PROMPT_CARD_CAROUSEL"
        {...autoAdvancePauseProps}
      >
        <WorkspaceAgentPromptCardCarouselControls
          labelId={labelId}
          listId={listId}
          onScrollNext={canScrollNext ? handleScrollNext : undefined}
          onScrollPrev={canScrollPrev ? handleScrollPrev : undefined}
        />
        {/* Bleeds past the message list's `px-4` to the panel edges; the matching padding keeps the first card on the text column. */}
        <ol
          aria-labelledby={labelId}
          className="no-scrollbar relative -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-2 overflow-x-auto px-4"
          id={listId}
          ref={scrollContainerRef}
        >
          {cards.map((card, index) => (
            <WorkspaceAgentPromptCardCarouselSlide
              buttonLabel={card.buttonLabel}
              id={card.id}
              index={index}
              isDisabled={isDisabled}
              key={card.id}
              onSelect={onSelect}
              thumbnails={card.thumbnails}
              subtitle={card.subtitle}
              title={card.title}
            />
          ))}
        </ol>
      </div>
    );
  },
);

WorkspaceAgentPromptCardCarousel.displayName = 'WorkspaceAgentPromptCardCarousel';
